/**
 * <rashi-wheel> — a circular zodiac showing grahas at their true longitudes.
 *
 * ◆ The one view in which a graha's **degree** is visible rather than only its
 * rāśi. The three square styles are categorical: they tell you Saturn is in
 * Vṛścika and nothing about whether it is at 0° or 29°. That difference decides
 * a nakṣatra, a pada and therefore a daśā, so a view that shows it is worth its
 * space — this is the chart that makes a gaṇḍānta or a graha on a rāśi cusp
 * immediately obvious.
 *
 * ⚠ Real longitudes mean real collisions: two grahas within a degree overlap. The
 * spreader below pushes labels apart along the ring while leaving the tick at the
 * true position, so the mark stays honest even when the label has moved.
 *
 * © Manish Jagdish Thatte
 */

import { SVG_NS, el } from '../utils/svg.js';
import { RASHIS, graha } from '../jyotish/constants.js';
import { localName } from '../utils/i18n.js';

const R_OUTER = 48, R_SIGNS = 41, R_TICK_IN = 34, R_TICK_OUT = 40, R_LABEL = 28;
const D2R = Math.PI / 180;

/** 0° Meṣa at the left, running anticlockwise — the usual orientation. */
function pointAt(lon, radius) {
  const a = (180 - lon) * D2R;
  return [50 + radius * Math.cos(a), 50 - radius * Math.sin(a)];
}

export class RashiWheel extends HTMLElement {
  static observedAttributes = ['locale'];

  connectedCallback() { this.render(); }
  attributeChangedCallback() { if (this.isConnected) this.render(); }

  set kundali(value) { this._kundali = value; this.render(); }
  get kundali() { return this._kundali ?? null; }

  render() {
    const k = this._kundali;
    this.textContent = '';
    if (!k) return;
    const locale = this.getAttribute('locale') || 'en';

    const svg = el('svg', {
      viewBox: '0 0 100 100', class: 'chart chart--wheel', role: 'img',
      'aria-label': 'Zodiac wheel with each graha at its sidereal longitude'
    });

    svg.append(el('circle', { cx: 50, cy: 50, r: R_OUTER, class: 'wheel__rim' }));
    svg.append(el('circle', { cx: 50, cy: 50, r: R_TICK_IN, class: 'wheel__rim wheel__rim--inner' }));

    // The twelve rāśi divisions and their labels.
    for (let i = 0; i < 12; i++) {
      const [x1, y1] = pointAt(i * 30, R_TICK_IN);
      const [x2, y2] = pointAt(i * 30, R_OUTER);
      svg.append(el('line', { x1, y1, x2, y2, class: 'wheel__spoke' }));
      const [lx, ly] = pointAt(i * 30 + 15, R_SIGNS);
      svg.append(el('text', {
        x: lx, y: ly, class: 'wheel__rashi', 'text-anchor': 'middle',
        'dominant-baseline': 'middle'
      }, RASHIS[i].abbr));
    }

    // Every 10° inside each rāśi, a short tick — enough to read a degree by eye.
    for (let d = 0; d < 360; d += 10) {
      if (d % 30 === 0) continue;
      const [x1, y1] = pointAt(d, R_TICK_IN);
      const [x2, y2] = pointAt(d, R_TICK_IN + 2.5);
      svg.append(el('line', { x1, y1, x2, y2, class: 'wheel__tick' }));
    }

    // The lagna, drawn as a full radius so it reads as an axis, not a point.
    if (k.lagna) {
      const [x1, y1] = pointAt(k.lagna.lon, 0);
      const [x2, y2] = pointAt(k.lagna.lon, R_OUTER);
      svg.append(el('line', { x1, y1, x2, y2, class: 'wheel__lagna' }));
      const [tx, ty] = pointAt(k.lagna.lon, R_OUTER + 3.4);
      svg.append(el('text', {
        x: tx, y: ty, class: 'wheel__lagna-label', 'text-anchor': 'middle',
        'dominant-baseline': 'middle'
      }, 'La'));
    }

    // Ticks at the true longitudes, then labels on a spread-out ring.
    const placed = spread(Object.values(k.grahas).map((g) => ({ id: g.graha, lon: g.lon })));
    for (const { id, lon, labelLon } of placed) {
      const info = k.grahas[id];
      const [x1, y1] = pointAt(lon, R_TICK_IN);
      const [x2, y2] = pointAt(lon, R_TICK_OUT);
      svg.append(el('line', { x1, y1, x2, y2, class: 'wheel__graha-tick' }));

      // ⚠ A leader line whenever the label was pushed, so the reader can see
      // that the text is not sitting at the true degree.
      if (Math.abs(shortest(labelLon - lon)) > 0.6) {
        const [lx1, ly1] = pointAt(lon, R_TICK_IN);
        const [lx2, ly2] = pointAt(labelLon, R_LABEL + 3);
        svg.append(el('line', { x1: lx1, y1: ly1, x2: lx2, y2: ly2, class: 'wheel__leader' }));
      }

      const [lx, ly] = pointAt(labelLon, R_LABEL);
      const text = el('text', {
        x: lx, y: ly, 'text-anchor': 'middle', 'dominant-baseline': 'middle',
        class: [
          'wheel__graha', info.retrograde ? 'is-retrograde' : null,
          info.combust ? 'is-combust' : null
        ].filter(Boolean).join(' '),
        'data-graha': id
      }, graha(id).name.symbol + (info.retrograde ? ' ℞' : ''));
      text.append(el('title', {}, `${localName(graha(id).name, locale)} ${formatShort(info)}`));
      svg.append(text);
    }

    this.append(svg);
  }
}

function formatShort(g) {
  const d = Math.floor(g.degreesInRashi);
  const m = Math.round((g.degreesInRashi - d) * 60);
  return `${g.rashi.abbr} ${d}°${String(m).padStart(2, '0')}′`;
}

const shortest = (d) => ((d + 540) % 360) - 180;

/**
 * Push overlapping labels apart along the ring.
 *
 * ◆ A single relaxation pass rather than a solver: grahas cluster in twos and
 * threes, not twenties, and a converging iterative layout here would be
 * machinery for a problem the data does not present. The tick stays at the true
 * longitude either way, so the worst case is a label a few degrees off with a
 * leader line pointing at where it belongs.
 */
function spread(items, minGap = 9) {
  const sorted = [...items].sort((a, b) => a.lon - b.lon).map((g) => ({ ...g, labelLon: g.lon }));
  for (let pass = 0; pass < 24; pass++) {
    let moved = false;
    for (let i = 0; i < sorted.length; i++) {
      const a = sorted[i];
      const b = sorted[(i + 1) % sorted.length];
      const gap = shortest(b.labelLon - a.labelLon);
      if (Math.abs(gap) < minGap) {
        const push = (minGap - Math.abs(gap)) / 2;
        a.labelLon = (a.labelLon - push + 360) % 360;
        b.labelLon = (b.labelLon + push) % 360;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return sorted;
}

customElements.define('rashi-wheel', RashiWheel);
